import { Drawer, Spin, Empty, Carousel, Tabs } from "antd";
import {
  ArrowLeft,
  Box,
  Edit,
  MapPin,
  Package,
  User,
  Calendar,
  History,
  X,
} from "lucide-react";
import { FC, useState, useEffect } from "react";
import { useGetInventory } from "~/services/inventory";
import { useGetHistoryServiceTicketsByInventoryId, useGetUpcomingServiceTicketsByInventoryId } from "~/services/service-ticket";
import { TInventory } from "~/types";
import { capitalize, formatDimensions, formatPackingType } from "~/utils";
import { InventoryStatusCode, InventoryType } from "~/utils/constants";
import { useInventoryStore } from "../inventory.store";
import dayjs from "dayjs";
import UpcomingServiceTickets from "./upcoming-service-tickets";
import HistoryServiceTickets from "./history-service-tickets";

type Props = {
  inventoryId?: string;
  open: boolean;
  onClose: () => void;
};

const getStatusColor = (status?: InventoryStatusCode) => {
  switch (status) {
    case InventoryStatusCode.AVAILABLE_LIVE:
      return "bg-green-100 text-green-800";
    case InventoryStatusCode.AVAILABLE_ON_HAND:
      return "bg-blue-100 text-blue-800";
    case InventoryStatusCode.NO_POSSESSION:
      return "bg-orange-100 text-orange-800";
    case InventoryStatusCode.RELEASED:
      return "bg-purple-100 text-purple-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const InventorySidebar: FC<Props> = ({ inventoryId, open, onClose }) => {
  const [activeId, setActiveId] = useState<string | undefined>(inventoryId);
  const [history, setHistory] = useState<string[]>([]);
  const { openInventoryModal } = useInventoryStore();

  // Sync with external inventoryId prop
  useEffect(() => {
    if (open && inventoryId) {
      setActiveId(inventoryId);
      setHistory([]);
    }
  }, [inventoryId, open]);

  const { data: inventory, isLoading } = useGetInventory(
    open ? activeId : undefined
  );

  const navigateTo = (id: string) => {
    if (activeId) {
      setHistory((prev) => [...prev, activeId!]);
    }
    setActiveId(id);
  };

  const goBack = () => {
    const prev = history[history.length - 1];
    if (prev) {
      setHistory((h) => h.slice(0, -1));
      setActiveId(prev);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={480}
      title={null}
      closeIcon={null}
      styles={{ body: { padding: 0 } }}
    >
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Spin size="large" />
        </div>
      ) : !inventory ? (
        <div className="flex items-center justify-center h-64">
          <Empty description="Inventory not found" />
        </div>
      ) : (
        <SidebarContent
          inventory={inventory}
          onClose={onClose}
          onEdit={(id) => {
            onClose();
            openInventoryModal(id);
          }}
          onNavigate={navigateTo}
          onBack={history.length > 0 ? goBack : undefined}
        />
      )}
    </Drawer>
  );
};

const SidebarContent: FC<{
  inventory: TInventory;
  onClose: () => void;
  onEdit: (id: string) => void;
  onNavigate: (id: string) => void;
  onBack?: () => void;
}> = ({ inventory, onClose, onEdit, onNavigate, onBack }) => {
  const { openInventoryModal } = useInventoryStore();
  const isPackage = inventory.itemType === InventoryType.PACKAGE;
  const containedCount = inventory.containedInventories?.length || 0;

  const { data: historyData } = useGetHistoryServiceTicketsByInventoryId(inventory.id);
  const { data: upcomingData } = useGetUpcomingServiceTicketsByInventoryId(inventory.id);
  const historyCount = historyData?.items?.length || 0;
  const upcomingCount = upcomingData?.items?.length || 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4 border-b bg-gray-50">
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800 mb-2"
          >
            <ArrowLeft size={14} />
            <span>Back</span>
          </button>
        )}
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              {isPackage ? (
                <Box size={18} className="text-amber-500 flex-shrink-0" />
              ) : (
                <Package size={18} className="text-green-500 flex-shrink-0" />
              )}
              <span className="text-xs font-mono text-gray-500">
                {inventory.inventoryRef}
              </span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(
                  inventory.status
                )}`}
              >
                {capitalize(inventory.status)}
              </span>
            </div>
            <h2 className="text-lg font-semibold truncate">
              {inventory.title || "Untitled"}
            </h2>
            {inventory.artistName && (
              <p className="text-sm text-gray-600">{inventory.artistName}</p>
            )}
          </div>
          <div className="flex items-center space-x-1 ml-3">
            <button
              onClick={() => onEdit(inventory.id)}
              className="p-1.5 rounded hover:bg-gray-200 text-gray-500"
              title="Edit"
            >
              <Edit size={16} />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-gray-200 text-gray-500"
              title="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        {/* Image carousel */}
        {inventory.images && inventory.images.length > 0 && (
          <div className="px-5 pt-4">
            <div className="bg-gray-100 rounded-lg overflow-hidden h-48">
              <Carousel autoplay autoplaySpeed={3000} arrows>
                {inventory.images.map((image, idx) => (
                  <div
                    key={image.id || idx}
                    className="h-48 flex items-center justify-center"
                  >
                    <img
                      src={image.url}
                      alt={`${inventory.title} - ${idx + 1}`}
                      className="max-w-full max-h-48 object-contain object-center"
                    />
                  </div>
                ))}
              </Carousel>
            </div>
          </div>
        )}

        {/* Details grid */}
        <div className="px-5 py-4 space-y-4">
          {/* Client */}
          <div className="flex items-start space-x-3">
            <User size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-xs text-gray-500">Client</div>
              <div className="text-sm font-medium">
                {inventory.client?.companyName ||
                  [
                    inventory.client?.firstName,
                    inventory.client?.lastName,
                  ]
                    .filter(Boolean)
                    .join(" ") ||
                  "N/A"}
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="flex items-start space-x-3">
            <MapPin size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-xs text-gray-500">Location</div>
              <div className="text-sm font-medium">
                {inventory.location?.name || "Not assigned"}
              </div>
            </div>
          </div>

          {/* In container */}
          {inventory.parentInventory && inventory.parentInventoryId && (
            <div className="flex items-start space-x-3">
              <Box size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs text-gray-500">Inside Container</div>
                <button
                  onClick={() => onNavigate(inventory.parentInventoryId!)}
                  className="text-sm font-medium text-blue-600 hover:underline"
                >
                  {inventory.parentInventory.title} ({inventory.parentInventory.inventoryRef})
                </button>
              </div>
            </div>
          )}

          {/* Info grid */}
          <div className="grid grid-cols-2 gap-3 pt-2 border-t">
            <DetailItem label="Type" value={capitalize(inventory.itemType)} />
            {isPackage && inventory.containerType && (
              <DetailItem
                label="Container Type"
                value={capitalize(inventory.containerType)}
              />
            )}
            <DetailItem label="Dimensions" value={formatDimensions(inventory)} />
            <DetailItem
              label="Weight"
              value={inventory.weight ? `${inventory.weight} lbs` : "-"}
            />
            <DetailItem
              label="Packing"
              value={formatPackingType(inventory)}
            />
            <DetailItem label="Parts" value={String(inventory.parts || 1)} />
            <DetailItem
              label="Value"
              value={
                inventory.value
                  ? `$${inventory.value.toLocaleString()}`
                  : "-"
              }
            />
            <DetailItem label="Medium" value={inventory.medium || "-"} />
            <DetailItem
              label="Client Ref"
              value={inventory.clientInternalRef || "-"}
            />
            <DetailItem
              label="Size (ft³)"
              value={
                inventory.length && inventory.width && inventory.height
                  ? (
                      (inventory.length * inventory.width * inventory.height) /
                      1728
                    ).toFixed(2)
                  : "-"
              }
            />
            <DetailItem
              label="Created"
              value={dayjs(inventory.createdAt).format("MMM D, YYYY")}
            />
            <DetailItem
              label="Updated"
              value={dayjs(inventory.updatedAt).format("MMM D, YYYY")}
            />
          </div>

          {/* Special handling */}
          {inventory.specialHandling && inventory.specialHandling.length > 0 && (
            <div className="pt-2 border-t">
              <div className="text-xs text-gray-500 mb-1">Special Handling</div>
              <div className="flex flex-wrap gap-1">
                {inventory.specialHandling.map((h, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs"
                  >
                    {capitalize(h)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {inventory.notes && (
            <div className="pt-2 border-t">
              <div className="text-xs text-gray-500 mb-1">Notes</div>
              <div className="text-sm p-2 bg-gray-50 rounded border">
                {inventory.notes}
              </div>
            </div>
          )}
        </div>

        {/* Contained inventories (for packages) */}
        {isPackage && (
          <div className="px-5 pb-4">
            <Tabs
              defaultActiveKey="contents"
              size="small"
              items={[
                {
                  key: "contents",
                  label: (
                    <span className="flex items-center space-x-1">
                      <Package size={14} />
                      <span>Contents ({containedCount})</span>
                    </span>
                  ),
                  children: (
                    <div className="space-y-2">
                      {containedCount === 0 ? (
                        <Empty
                          description="No items in this package"
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      ) : (
                        inventory.containedInventories.map((item) => (
                          <ContainedItemCard
                            key={item.id}
                            item={item}
                            onView={(id) => onNavigate(id)}
                            onEdit={(id) => openInventoryModal(id)}
                          />
                        ))
                      )}
                    </div>
                  ),
                },
                {
                  key: "upcoming",
                  label: (
                    <span className="flex items-center space-x-1">
                      <Calendar size={14} />
                      <span>Upcoming ({upcomingCount})</span>
                    </span>
                  ),
                  children: <UpcomingServiceTickets inventoryId={inventory.id} />,
                },
                {
                  key: "history",
                  label: (
                    <span className="flex items-center space-x-1">
                      <History size={14} />
                      <span>History ({historyCount})</span>
                    </span>
                  ),
                  children: <HistoryServiceTickets inventoryId={inventory.id} />,
                },
              ]}
            />
          </div>
        )}

        {/* For pieces — just show tickets tabs */}
        {!isPackage && (
          <div className="px-5 pb-4">
            <Tabs
              defaultActiveKey="upcoming"
              size="small"
              items={[
                {
                  key: "upcoming",
                  label: (
                    <span className="flex items-center space-x-1">
                      <Calendar size={14} />
                      <span>Upcoming ({upcomingCount})</span>
                    </span>
                  ),
                  children: <UpcomingServiceTickets inventoryId={inventory.id} />,
                },
                {
                  key: "history",
                  label: (
                    <span className="flex items-center space-x-1">
                      <History size={14} />
                      <span>History ({historyCount})</span>
                    </span>
                  ),
                  children: <HistoryServiceTickets inventoryId={inventory.id} />,
                },
              ]}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const DetailItem: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-sm font-medium">{value}</div>
  </div>
);

const ContainedItemCard: FC<{
  item: TInventory;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
}> = ({ item, onView, onEdit }) => (
  <div
    className="p-3 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
    onClick={() => onView(item.id)}
  >
    <div className="flex items-start justify-between">
      <div className="flex items-start space-x-2 min-w-0">
        <Package size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">
            {item.title || "Untitled"}
          </div>
          <div className="text-xs text-gray-500">
            {item.inventoryRef}
            {item.artistName && ` • ${item.artistName}`}
          </div>
        </div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onEdit(item.id);
        }}
        className="p-1 text-gray-400 hover:text-blue-600 flex-shrink-0"
        title="Edit"
      >
        <Edit size={14} />
      </button>
    </div>
    <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-gray-500">
      <div>
        {item.length ?? 0}" × {item.width ?? 0}" × {item.height ?? 0}"
      </div>
      <div>{item.medium || "-"}</div>
      <div>
        {item.value ? `$${item.value.toLocaleString()}` : "-"}
      </div>
    </div>
  </div>
);
